import { z } from 'zod';
import { JOB_REF_PATTERN, OPPORTUNITY_REF_PATTERN } from './job-ref.js';
/** Kind is explicit: some legacy O-/P- references also exist in the Job book.
 * A title or numeric suffix cannot decide which authoritative record was meant. */
export const WorkbenchRecordTarget = z.object({
    kind: z.enum(['opportunity', 'job']).describe('Explicit record book; legacy O-/P- references may also be Jobs.'),
    reference: z.string().trim().toUpperCase().regex(JOB_REF_PATTERN).describe('Exact saved reference, including its prefix; never a numeric suffix or title.'),
}).strict().superRefine((target, ctx) => {
    if (target.kind === 'opportunity' && !OPPORTUNITY_REF_PATTERN.test(target.reference))
        ctx.addIssue({ code: 'custom', path: ['reference'], message: 'An Opportunity requires its saved O- or P- reference' });
});
/** Identity only. This does not claim any related module has been loaded. */
export const WorkbenchRecordIdentity = z.object({
    contract: z.literal('aiw.workbench.record/1'),
    actor_id: z.uuid(), test_set_id: z.uuid().nullable(),
    target: WorkbenchRecordTarget,
    record_key: z.string().min(1), title: z.string().min(1),
    source: z.object({ view: z.enum(['crm.v_job', 'crm.v_opportunity']), primary_key: z.string().min(1),
        fingerprint: z.string().regex(/^[a-f0-9]{64}$/), observed_at: z.iso.datetime({ offset: true }) }).strict(),
}).strict().superRefine((record, ctx) => {
    const kind = record.target.kind, view = kind === 'job' ? 'crm.v_job' : 'crm.v_opportunity';
    if (record.source.view !== view || record.record_key !== kind + ':' + record.source.primary_key)
        ctx.addIssue({ code: 'custom', message: 'Record key and source identity disagree' });
    if (kind === 'job' && record.source.primary_key !== record.target.reference)
        ctx.addIssue({ code: 'custom', message: 'A Job uses its canonical reference as its primary key' });
    if (kind === 'opportunity' && !z.uuid().safeParse(record.source.primary_key).success)
        ctx.addIssue({ code: 'custom', message: 'An Opportunity requires its immutable ID' });
});
/** Shared identity shaping for the user-scoped API and the published assistant
 * lookup. The descriptor contains no actor claim or permission grant. */
export function workbenchRecordDescriptor(target, row) {
    const job = target.kind === 'job', key = job ? row.ref : row.id, name = job ? row.name : row.contact_name;
    if (row.ref !== target.reference || typeof key !== 'string' || (!job && !z.uuid().safeParse(key).success))
        throw Error('Invalid source identity');
    return { target, record_key: target.kind + ':' + key, title: typeof name === 'string' && name.trim() ? name : target.reference,
        source: { view: job ? 'crm.v_job' : 'crm.v_opportunity', primary_key: key } };
}
//# sourceMappingURL=workbench-record.js.map